import { z } from 'zod';
import { MAX_STOCK_QUANTITY, normalizeStockQuantity } from './stock-summary';
import {
  vehicleEntryFitsSource,
  vehicleLineKey,
  type VehicleDocumentLike,
  type VehicleDocumentLineLike,
} from './vehicle-documents';

const uuid = z.string().uuid();
const quantity = z
  .number()
  .finite()
  .positive()
  .max(MAX_STOCK_QUANTITY)
  .refine(
    (value) =>
      Math.abs(value - normalizeStockQuantity(value)) <=
      Number.EPSILON * Math.max(1, Math.abs(value)) * 2,
    'vehicle_stock_quantity_precision',
  )
  .transform(normalizeStockQuantity)
  .refine((value) => value > 0, 'vehicle_stock_quantity_positive');

const vehicleStockLineSchema = z.object({
  articleId: uuid,
  variantId: uuid.nullish().transform((value) => value ?? null),
  variantLabel: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  quantity,
  // Saved source lines use null; command lines may omit notes entirely.
  notes: z.string().nullish().transform((value) => value ?? undefined),
});

const vehicleStockLinesSchema = z
  .array(vehicleStockLineSchema)
  .min(1)
  .superRefine((lines, context) => {
    const totals = new Map<string, number>();
    for (const [index, line] of lines.entries()) {
      const key = vehicleLineKey(line);
      const total = normalizeStockQuantity(
        (totals.get(key) ?? 0) + line.quantity,
      );
      if (total > MAX_STOCK_QUANTITY) {
        context.addIssue({
          code: 'custom',
          path: [index, 'quantity'],
          message: 'vehicle_stock_quantity_too_high',
        });
      }
      totals.set(key, total);
    }
  });

/** Canonical command payload. Policy activation and actor authority are not input fields. */
export const vehicleStockDocumentSchema = z
  .object({
    storeId: uuid.optional(),
    clientRequestId: uuid.optional(),
    documentType: z.enum(['exit', 'entry']).default('exit'),
    warehouseId: uuid.nullish().transform((value) => value ?? null),
    carRegistrationNumber: z.string().trim().max(50).default(''),
    exitDate: z.iso.date(),
    parentDocumentId: uuid.nullish().transform((value) => value ?? null),
    notes: z.string().optional(),
    customProperties: z
      .record(
        z.string(),
        z.union([z.string(), z.number().finite(), z.boolean(), z.null()]),
      )
      .default({}),
    lines: vehicleStockLinesSchema,
  })
  .superRefine((input, context) => {
    if ((input.documentType === 'entry') !== Boolean(input.parentDocumentId)) {
      context.addIssue({
        code: 'custom',
        path: ['parentDocumentId'],
        message: 'vehicle_stock_source_required',
      });
    }
  });

export type VehicleStockDocumentInput = z.infer<
  typeof vehicleStockDocumentSchema
>;

export class VehicleStockValidationError extends Error {
  constructor(public readonly code: string) {
    super(code);
    this.name = 'VehicleStockValidationError';
  }
}

export interface VehicleStockWarehouseLike {
  id: string;
  storeId: string;
  isDefault?: boolean;
}

/** The caller supplies only warehouses returned by its authorized data service. */
export function resolveVehicleStockWarehouse(
  storeId: string,
  warehouses: readonly VehicleStockWarehouseLike[],
  requestedWarehouseId?: string | null,
): string {
  if (!uuid.safeParse(storeId).success) {
    throw new VehicleStockValidationError('invalid_stock_target');
  }
  const available = warehouses.filter(
    (warehouse) => warehouse.storeId === storeId,
  );
  if (requestedWarehouseId !== undefined && requestedWarehouseId !== null) {
    if (
      !uuid.safeParse(requestedWarehouseId).success ||
      !available.some((warehouse) => warehouse.id === requestedWarehouseId)
    ) {
      throw new VehicleStockValidationError('invalid_stock_target');
    }
    return requestedWarehouseId;
  }
  if (available.length === 0) {
    throw new VehicleStockValidationError('vehicle_stock_warehouse_required');
  }
  const defaults = available.filter(
    (warehouse) => warehouse.isDefault === true,
  );
  const selected =
    defaults.length === 1
      ? defaults[0]
      : available.length === 1
        ? available[0]
        : undefined;
  if (!selected) {
    throw new VehicleStockValidationError('vehicle_stock_warehouse_ambiguous');
  }
  if (!uuid.safeParse(selected.id).success) {
    throw new VehicleStockValidationError('invalid_stock_target');
  }
  return selected.id;
}

export interface VehicleStockEffect {
  articleId: string;
  variantId: string | null;
  warehouseId: string;
  quantity: number;
  movementType: 'out' | 'in';
}

export type VehicleStockSource = VehicleDocumentLike & {
  warehouseId?: string | null;
  /** Persisted issuance fact; the current tenant policy cannot replace this fact. */
  stockDeducted?: boolean;
};

/**
 * Plans an opted-in issuance only. The adapter must persist these effects with
 * the document atomically and recheck the source under a transaction lock.
 * This pure calculation neither activates tracking nor mutates existing stock.
 */
export function buildVehicleStockEffects(input: {
  documentType: 'exit' | 'entry';
  warehouseId?: string | null;
  parentDocumentId?: string | null;
  lines: readonly VehicleDocumentLineLike[];
  source?: VehicleStockSource;
  documents?: readonly VehicleDocumentLike[];
}): VehicleStockEffect[] {
  const parsedLines = vehicleStockLinesSchema.safeParse(input.lines);
  if (
    !parsedLines.success ||
    (input.documentType !== 'exit' && input.documentType !== 'entry')
  ) {
    throw new VehicleStockValidationError('invalid_stock_target');
  }

  let warehouseId = input.warehouseId;
  if (input.documentType === 'entry') {
    const source = input.source;
    if (
      !source ||
      source.documentType !== 'exit' ||
      !uuid.safeParse(source.id).success ||
      (input.parentDocumentId != null && input.parentDocumentId !== source.id)
    ) {
      throw new VehicleStockValidationError('vehicle_stock_source_required');
    }
    const documents = input.documents ?? [];
    const validHistory =
      vehicleStockLinesSchema.safeParse(source.lines).success &&
      documents
        .filter(
          (document) =>
            document.documentType === 'entry' &&
            document.parentDocumentId === source.id,
        )
        .every(
          (document) =>
            vehicleStockLinesSchema.safeParse(document.lines).success,
        );
    if (!validHistory) {
      throw new VehicleStockValidationError('invalid_stock_target');
    }
    if (!vehicleEntryFitsSource(source, documents, parsedLines.data)) {
      throw new VehicleStockValidationError('vehicle_entry_quantity_too_high');
    }
    // A historical loading record never removed stock. Its return cannot add any.
    if (source.stockDeducted !== true) return [];
    if (
      !uuid.safeParse(source.warehouseId).success ||
      (warehouseId != null && warehouseId !== source.warehouseId)
    ) {
      throw new VehicleStockValidationError('invalid_stock_target');
    }
    warehouseId = source.warehouseId;
  } else if (input.parentDocumentId != null || input.source) {
    throw new VehicleStockValidationError('vehicle_stock_source_required');
  }

  if (!uuid.safeParse(warehouseId).success) {
    throw new VehicleStockValidationError('invalid_stock_target');
  }
  const validatedWarehouseId = warehouseId as string;
  return parsedLines.data.map((line) => ({
    articleId: line.articleId,
    variantId: line.variantId,
    warehouseId: validatedWarehouseId,
    quantity: line.quantity,
    movementType: input.documentType === 'exit' ? 'out' : 'in',
  }));
}
